<template>
  <div class="ce-searchResult ce-searchResult-pigment">
    <div class="ce-searchResult-inner">
      <span class="ce-searchResult-colorBadgeHolder">
        <a :href="link">
          <img
            class="ce-searchResult-colorBadge"
            v-if="colorImage"
            :src="colorImage"
            :alt="pigment.product_name">
        </a>
      </span>

      <div class="flex-auto">
        <span class="ce-searchResult-pigmentName">
          <a :href="link">
            {{ pigment.product_name }}
          </a>
          <span class="ce-searchResult-internalFlag">
            <fa-icon
              v-if="pigment.isInternal"
              transform="up-6 shrink-4"
              icon="lock" />

            <fa-icon
              v-if="pigment.isActive === false"
              transform="up-6 shrink-4"
              icon="power-off" />
          </span>
        </span>
      </div>

      <div
        class="ce-searchResult-actions"
        v-if="hasActions">
        <a
          class="ce-searchResultComparisonAction"
          :class="{disabled: !canAddToComparison}"
          href=""
          @click.prevent="addToComparison">
          <tooltip
            v-if="comparisonListTooltip"
            :content="comparisonListTooltip">
            <fa-layers>
              <fa-icon icon="list" />
              <fa-icon
                v-if="!isOnCompareList"
                icon="plus-circle"
                transform="shrink-4 up-7 right-10" />
              <fa-icon
                v-else
                icon="check"
                transform="shrink-2 up-7 right-10" />
            </fa-layers>
          </tooltip>
          <fa-layers v-else>
            <fa-icon icon="list" />
            <fa-icon
              v-if="!isOnCompareList"
              icon="plus-circle"
              transform="shrink-4 up-7 right-10" />
            <fa-icon
              v-else
              icon="check"
              transform="shrink-2 up-7 right-10" />
          </fa-layers>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  props: {
    pigment: {
      type: Object,
      required: true
    },
    forIndustry: {
      type: String,
      required: false,
      default: null
    }
  },
  computed: {
    ...mapGetters('productComparison', {
      compareListProductIds: 'productIds'
    }),
    ...mapGetters('productComparison', ['basketLimitReached', 'activeComparisonIndustry']),
    ...mapState('productComparison', ['activeComparison']),
    ...mapState('generic', ['services']),
    colorImage() {
      if (this.pigment.stackPreviewImage?.url) {
        return this.pigment.stackPreviewImage.url;
      }

      if (this.pigment.image_color_chip) {
        return `${this.services.documentservice.url}/api/v2/document/download/${this.pigment.image_color_chip.id}`;
      }

      if (this.pigment.color_values
          && typeof this.pigment.color_values === 'object'
          && this.pigment.color_values.hasOwnProperty('image')
          && this.pigment.color_values.image) {
        return this.pigment.color_values.image;
      }

      return null;
    },
    link() {
      if (!this.forIndustry) return this.pigment.link;

      const { link } = this.pigment;
      const token = link.indexOf('?') === -1 ? '?' : '&';

      return `${link}${token}i=${this.forIndustry}`;
    },
    canAddToComparison() {
      return !this.isOnCompareList && !this.basketLimitReached && this.isSameIndustryAsComparison;
    },
    isOnCompareList() {
      return this.compareListProductIds.indexOf(parseInt(this.pigment.id)) > -1;
    },
    isSameIndustryAsComparison() {
      if (!this.activeComparison || !this.activeComparison.industry) return true;

      return this.pigment.industries.indexOf(this.activeComparison.industry) > -1;
    },
    hasActions() {
      return true;
    },
    comparisonListTooltip() {
      if (this.canAddToComparison || this.isOnCompareList) return null;

      if (this.basketLimitReached) return this.$t('productComparison.basket.tooltips.limitReached');

      if (!this.isSameIndustryAsComparison) {
        return this.$t('productComparison.basket.tooltips.industryLimit', { industry: this.activeComparisonIndustry.title });
      }

      return null;
    }
  },
  methods: {
    addToComparison() {
      if (!this.canAddToComparison) return;

      // Check if a industry selection is necessary
      if (!this.activeComparison || !this.activeComparison.industry) {
        let industry = null;

        // Pigment has exactly one industry
        if (this.pigment.industries.length === 1) {
          [industry] = this.pigment.industries;
        } else if (this.forIndustry) {
          // Exactly one industry filter active, so use that one
          industry = this.forIndustry;
        }

        this.$store.dispatch('productComparison/addToBasket', {
          pigment: this.pigment,
          industry
        });
      } else {
        this.$store.dispatch('productComparison/addToBasket', {
          pigment: this.pigment
        });
      }
    }
  }
};
</script>

<style lang="less">
  @import (reference) '~styles/includes/base';

  .ce-searchResult-pigment {
    display: flex;
    align-items: center;
    border-bottom: 1px solid @color-mono-92;
    transition: all 250ms ease-in-out;

    .ce-searchResult-inner {
      .flex-auto;
      align-items: center;
      display: flex;
      padding: @space-3 @space-2;
    }

    a {
      text-decoration: none;
    }

    .ce-searchResult-actions {
      justify-self: flex-end;
      padding: 0 @space-1 0 @space-3;
      display: flex;

      > * {
        padding: 0 @space-1;
      }

      .ce-searchResultComparisonAction {
        .fa-list, .fa-plus-circle {
          color: @color-mono-60;
          transition: color 250ms ease-in-out;
        }

        .fa-check {
          color: @color-success;
        }

        &.disabled {
          cursor: default;

          .fa-plus-circle {
            color: @color-mono-60;
          }

          .fa-plus-circle, .fa-list {
            opacity: 0.3;
          }

          .fa-check {
            opacity: 0.7;
          }
        }

        &:not(.disabled):hover {
          .fa-list {
            color: @color-text-base;
          }

          .fa-plus-circle {
            color: @color-light;
          }
        }
      }
    }

    .ce-searchResult-pigmentName {
      a {
        color: @color-text-base;
      }
    }

    .ce-searchResult-internalFlag {
      color: @color-text-base;
    }

    .ce-searchResult-colorBadgeHolder {
      width: 10px;
      height: 20px;
      border-radius: @border-radius;
      overflow: hidden;
      margin-right: @space-2;

      a {
        display: block;
        width: 100%;
        height: 100%;
      }
    }

    .ce-searchResult-colorBadge {
      width: 100%;
      height: 100%;
    }

    &:hover {
      background: @color-mono-92;
    }
  }
</style>
